import { useCallback } from "react";
import {
  isSignedIn,
  useBypassEmail,
  useSession,
  type Claims,
} from "@jkast90/kast-common/auth";
import { getAuthEnabled } from "../settings";
import { bypassEmail } from "../auth/bypassEmail";

/**
 * The name the API stores as owner_email, by the API's own rule
 * (api-rs/src/auth.rs auth_user_from_claims): the first non-empty of the
 * `email`, `cognito:username`, `sub` and `username` claims. Nothing is
 * stripped: a provider-prefixed username ("Google_123") is the API's name
 * for that user too, so ownership checks (`owner_email === userEmail`) must
 * compare it whole. Display-only prefix stripping is normalizeEmail's job.
 */
export function emailOf(user: Claims): string {
  const claim = (key: string) => {
    const value = user.claims[key];
    return typeof value === "string" ? value : "";
  };
  return (
    user.email ||
    claim("cognito:username") ||
    user.sub ||
    claim("username") ||
    "unknown"
  );
}

/**
 * Who is signed in: the shared session (`useSession` holds the last
 * signed-in state while a sign-out leaves the page, so nothing flashes signed
 * out), or with auth disabled the bypass email. `setUserEmail` saves the
 * bypass email, and the Settings "Dashboard User" field.
 */
export function useAuth() {
  const state = useSession();
  const bypass = useBypassEmail(bypassEmail);
  const authOn = getAuthEnabled();
  const claims = authOn && isSignedIn(state) ? state.user : null;
  const isAuthenticated = authOn ? !!claims : !!bypass;
  const isLoading = authOn && state.status === "loading";
  return {
    userEmail: authOn ? (claims ? emailOf(claims) : "") : bypass,
    setUserEmail: useCallback((email: string) => bypassEmail.set(email), []),
    isAuthenticated,
    isLoading,
    /** Signed in and the start-up check is done: safe to load data. */
    isReady: isAuthenticated && !isLoading,
    /** The id token's claims (sub, email, groups); null when signed out, or with auth disabled. */
    user: claims,
  };
}

// Alias for backward compatibility
export const useUser = useAuth;
